import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { RadioButton } from 'primereact/radiobutton';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LMS_SUPPORTED_LANGUAGES } from '../constants/lmsLanguages';

// Çeviri metinleri için karakter sınırı
const TRANSLATION_MAX_LENGTH = 1000;

const SUPPORTED_LANGUAGES = LMS_SUPPORTED_LANGUAGES;

const getFlagClassName = (languageCode) => {
    const codeToFlagSuffixMap = { en: 'us', tr: 'tr', de: 'de', fr: 'fr', it: 'it', ro: 'ro' };
    const suffix = codeToFlagSuffixMap[languageCode];
    return suffix ? `fi fi-${suffix}` : 'fi fi-xx';
};

const blankTranslations = () => SUPPORTED_LANGUAGES.map((lang) => ({ languageId: lang.id, text: '' }));

// Eğitim Tipi / Eğitim Lokasyonu gibi ekranlar aynı formu paylaşır — sadece groupCode ve
// başlık/label metinleri farklıdır.
const LmsLookupDefinitionForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, showCodeField = false, showSortField = false, showActiveField = false, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const [formData, setFormData] = useState({
        id: null,
        code: '',
        sort: null,
        isActive: 1,
        translations: blankTranslations()
    });
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = !!(initialData && initialData.id);

    useEffect(() => {
        if (!visible) return;

        const backendTranslations = (initialData && initialData.translations) || [];
        const mergedTranslations = SUPPORTED_LANGUAGES.map((lang) => {
            const existing = backendTranslations.find((t) => t.languageId === lang.id);
            return existing ? { languageId: lang.id, text: existing.text || '' } : { languageId: lang.id, text: '' };
        });

        setFormData({
            id: (initialData && initialData.id) ?? null,
            code: (initialData && initialData.code) || '',
            sort: (initialData && initialData.sort) ?? null,
            isActive: initialData && initialData.isActive !== undefined && initialData.isActive !== null ? Number(initialData.isActive) : 1,
            translations: mergedTranslations
        });
        setLocalSubmitted(false);
        setErrors({});
    }, [visible, initialData]);

    const handleTranslationChange = (languageId, newText) => {
        setFormData((prev) => ({
            ...prev,
            translations: prev.translations.map((t) => (t.languageId === languageId ? { ...t, text: newText } : t))
        }));

        const lang = SUPPORTED_LANGUAGES.find((l) => l.id === languageId);
        if (lang && errors['translation_' + lang.code] && newText.trim()) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next['translation_' + lang.code];
                return next;
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const newErrors = {};
        const validationMessages = [];

        // Tüm 6 dil zorunlu — bkz. docs/LMS-Lookup-Translation-API.md §4.3
        SUPPORTED_LANGUAGES.forEach((lang) => {
            const translation = formData.translations.find((t) => t.languageId === lang.id);
            if (!translation || !translation.text.trim()) {
                const msg = th('lmsLookupDefinitions.translationRequired', { language: lang.name });
                newErrors['translation_' + lang.code] = msg;
                validationMessages.push(msg);
            }
        });

        // Kod, oluşturmada zorunlu — Update ucu code'u değiştirmiyor (bkz. LMS-Arac-Modelleri.md §3),
        // bu yüzden düzenlemede tekrar doğrulanmıyor (alan zaten disabled).
        if (showCodeField && !isEdit && !formData.code.trim()) {
            const msg = th('lmsLookupDefinitions.codeRequired');
            newErrors.code = msg;
            validationMessages.push(msg);
        }

        setErrors(newErrors);

        if (validationMessages.length > 0) {
            validationMessages.forEach((detail, index) => {
                setTimeout(() => {
                    toast.current?.show({ severity: 'error', summary: th('lmsLookupDefinitions.missingField'), detail, life: 4000 });
                }, index * 150);
            });
            return;
        }

        const payload = {
            translations: formData.translations.map((t) => ({ languageId: t.languageId, text: t.text.trim() }))
        };

        if (isEdit) {
            // Update ucu `id` üzerinden çalışır (doc §4.4)
            payload.id = formData.id;
        }

        // `code` yalnızca oluştururken gönderilir — LMS-Arac-Modelleri.md'de Update isteği
        // code içermiyor, yani backend'de değiştirilemez (immutable) kabul ediliyor.
        if (showCodeField && !isEdit) {
            payload.code = formData.code.trim();
        }

        if (showSortField) {
            payload.sort = Number(formData.sort) || 0;
        }

        if (showActiveField) {
            payload.isActive = Number(formData.isActive);
        }

        onSubmit(payload);
    };

    const renderFooter = () => (
        <div>
            {isEdit && handleDelete && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined type="button" />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
        </div>
    );

    return (
        <Dialog visible={visible} footer={renderFooter()} style={{ width: '30vw' }} header={isEdit ? th('lmsLookupDefinitions.editTitle') : th('lmsLookupDefinitions.addTitle')} modal className="p-fluid" onHide={onHide} key={formData.id ?? 'new'}>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit} className="p-fluid">
                <div className="flex flex-column gap-3">
                    {(showCodeField || showSortField || showActiveField) && (
                        <div className="grid">
                            {showCodeField && (
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="lms-lookup-code">
                                            {th('lmsLookupDefinitions.columnCode')} <span className="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            id="lms-lookup-code"
                                            value={formData.code}
                                            maxLength={50}
                                            disabled={isEdit}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
                                            className={localSubmitted && errors.code ? 'p-invalid' : ''}
                                        />
                                        {localSubmitted && errors.code && <small className="p-error">{errors.code}</small>}
                                    </div>
                                </div>
                            )}
                            {showSortField && (
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="lms-lookup-sort">{th('lmsLookupDefinitions.columnSort')}</label>
                                        <InputNumber id="lms-lookup-sort" value={formData.sort} min={0} onValueChange={(e) => setFormData((prev) => ({ ...prev, sort: e.value }))} />
                                    </div>
                                </div>
                            )}
                            {showActiveField && (
                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label>{th('common.status')}</label>
                                        <div className="flex gap-4 mt-2">
                                            <div className="flex align-items-center">
                                                <RadioButton
                                                    inputId="lms-lookup-active-yes"
                                                    name="lms-lookup-isActive"
                                                    value={1}
                                                    onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))}
                                                    checked={Number(formData.isActive) === 1}
                                                />
                                                <label htmlFor="lms-lookup-active-yes" className="ml-2 cursor-pointer">
                                                    {th('common.active', { fallback: 'Aktif' })}
                                                </label>
                                            </div>
                                            <div className="flex align-items-center">
                                                <RadioButton
                                                    inputId="lms-lookup-active-no"
                                                    name="lms-lookup-isActive"
                                                    value={0}
                                                    onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.value }))}
                                                    checked={Number(formData.isActive) === 0}
                                                />
                                                <label htmlFor="lms-lookup-active-no" className="ml-2 cursor-pointer">
                                                    {th('common.inactive', { fallback: 'Pasif' })}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="grid">
                        {SUPPORTED_LANGUAGES.map((lang) => {
                            const translation = formData.translations.find((t) => t.languageId === lang.id);
                            const currentText = (translation && translation.text) || '';
                            const inputId = `lms-translation-${lang.code}`;
                            const hasError = !!errors['translation_' + lang.code];
                            const showError = localSubmitted && (hasError || !currentText.trim());
                            const charCount = currentText.length;

                            return (
                                <div className="col-12" key={lang.id}>
                                    <div className="field">
                                        <label htmlFor={inputId} className="flex justify-content-between align-items-center">
                                            <span>
                                                <span className={getFlagClassName(lang.code)} style={{ fontSize: '1.2em', marginRight: '0.5em' }} title={lang.name} />
                                                {lang.name} <span className="text-red-500">*</span>
                                            </span>
                                            <small className={charCount >= TRANSLATION_MAX_LENGTH ? 'text-red-500' : 'text-color-secondary'}>
                                                {charCount}/{TRANSLATION_MAX_LENGTH}
                                            </small>
                                        </label>
                                        <InputText id={inputId} value={currentText} maxLength={TRANSLATION_MAX_LENGTH} onChange={(e) => handleTranslationChange(lang.id, e.target.value)} className={showError ? 'p-invalid' : ''} />
                                        {showError && <small className="p-error">{errors['translation_' + lang.code] || th('lmsLookupDefinitions.translationRequired', { language: lang.name })}</small>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </form>
        </Dialog>
    );
};

export default LmsLookupDefinitionForm;
